/** Date/time format helpers, ported VERBATIM from trexa-web.html. */

export const MON  = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONF = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DOW  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function pad2(n: number): string { return n < 10 ? '0' + n : '' + n; }

export function isoDate(d: Date): string {
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

/**
 * Parse a date string into a local Date. Robust to the two formats seen in this app:
 *  - ISO `yyyy-mm-dd` (optionally with a time) — the mockup / form format.
 *  - `MM/DD/YYYY HH:mm:ss` — how the backend GraphQL API serializes SQL date/datetime
 *    columns. Without this branch every API date sorts/compares wrong (all bookings look
 *    "past", "Next events" empties out, etc.).
 * Returns an Invalid Date for unparseable input.
 */
export function parseISO(s: string): Date {
  if (!s) return new Date(NaN);
  const t = ('' + s).trim();
  const slash = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);   // API: MM/DD/YYYY [HH:mm:ss]
  if (slash) return new Date(+slash[3], +slash[1] - 1, +slash[2]);
  const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);        // ISO: yyyy-mm-dd [THH:mm:ss]
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const d = new Date(t);
  return isNaN(d.getTime()) ? new Date(NaN) : d;
}

/**
 * Normalize any supported date string (API `MM/DD/YYYY …` or ISO) to canonical
 * `yyyy-mm-dd`. Use before any string comparison/sort against an ISO date
 * (e.g. `toIso(b.eventDate) >= isoDate(new Date())`). Empty string for invalid input.
 */
export function toIso(s: string | null | undefined): string {
  if (!s) return '';
  const d = parseISO('' + s);
  return isNaN(d.getTime()) ? '' : isoDate(d);
}

/** 'yyyy-mm-dd' → '3 Jan 2026'. */
export function fmtDateLong(s: string): string {
  if (!s) return '';
  const d = parseISO(s);
  return d.getDate() + ' ' + MON[d.getMonth()] + ' ' + d.getFullYear();
}

/** 'HH:MM' → '11:00 AM'. */
export function fmtTime(t: string | number): string {
  if (!t) return '';
  const p = ('' + t).split(':');
  const h = parseInt(p[0], 10) || 0;
  const m = p[1] || '00';
  const ap = h < 12 ? 'AM' : 'PM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return h12 + ':' + m + ' ' + ap;
}

/** 'yyyy-mm-dd' → weekday name. */
export function dowName(s: string): string {
  if (!s) return '';
  return DOW[parseISO(s).getDay()];
}
